import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  MapPinIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
} from '@heroicons/react/24/outline';
import toast, { Toaster } from 'react-hot-toast';
import type { AmbulanceVendorServiceArea } from '../../../models/MasterAdmin/ambulanceBookings';
import {
  createAmbulanceServiceArea,
  deleteAmbulanceServiceArea,
  fetchAmbulanceServiceAreas,
  updateAmbulanceServiceArea,
} from '../../../services/MasterAdmin/ambulanceService';

type FormState = {
  pinCode: string;
  areaName: string;
  latitude: string;
  longitude: string;
  radiusKm: string;
  isActive: boolean;
};

const emptyForm: FormState = {
  pinCode: '',
  areaName: '',
  latitude: '',
  longitude: '',
  radiusKm: '',
  isActive: true,
};

const AmbulanceVendorServiceAreasPage: React.FC = () => {
  const { vendorId } = useParams<{ vendorId: string }>();
  const navigate = useNavigate();
  const [rows, setRows] = useState<AmbulanceVendorServiceArea[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<AmbulanceVendorServiceArea | null>(null);
  const [form, setForm] = useState<FormState>(emptyForm);

  const load = useCallback(async () => {
    if (!vendorId) return;
    try {
      setLoading(true);
      const res = await fetchAmbulanceServiceAreas(vendorId);
      setRows(Array.isArray(res.serviceAreas) ? res.serviceAreas : []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load service areas.');
    } finally {
      setLoading(false);
    }
  }, [vendorId]);

  useEffect(() => {
    load();
  }, [load]);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (row: AmbulanceVendorServiceArea) => {
    setEditing(row);
    setForm({
      pinCode: row.pinCode ?? '',
      areaName: row.areaName ?? '',
      latitude: row.latitude != null ? String(row.latitude) : '',
      longitude: row.longitude != null ? String(row.longitude) : '',
      radiusKm: row.radiusKm != null ? String(row.radiusKm) : '',
      isActive: row.isActive,
    });
    setModalOpen(true);
  };

  const buildPayload = (): Record<string, unknown> | null => {
    const pinDigits = form.pinCode.replace(/\D/g, '');
    const lat = form.latitude.trim() === '' ? undefined : Number(form.latitude);
    const lng = form.longitude.trim() === '' ? undefined : Number(form.longitude);
    const rk = form.radiusKm.trim() === '' ? undefined : Number(form.radiusKm);

    if (lat !== undefined && !Number.isFinite(lat)) {
      toast.error('Latitude must be a valid number.');
      return null;
    }
    if (lng !== undefined && !Number.isFinite(lng)) {
      toast.error('Longitude must be a valid number.');
      return null;
    }
    if (rk !== undefined && !Number.isFinite(rk)) {
      toast.error('Radius (km) must be a valid number.');
      return null;
    }

    const hasPin = pinDigits.length > 0;
    const hasGeo =
      lat !== undefined &&
      lng !== undefined &&
      rk !== undefined &&
      Number.isFinite(lat) &&
      Number.isFinite(lng) &&
      Number.isFinite(rk) &&
      rk > 0;

    if (!hasPin && !hasGeo) {
      toast.error('Enter a pincode, or latitude + longitude + radius (km > 0).');
      return null;
    }

    const payload: Record<string, unknown> = {
      areaName: form.areaName.trim() || null,
      isActive: form.isActive,
    };
    if (hasPin) payload.pinCode = pinDigits;
    if (lat !== undefined) payload.latitude = lat;
    if (lng !== undefined) payload.longitude = lng;
    if (rk !== undefined) payload.radiusKm = rk;

    return payload;
  };

  const handleSave = async () => {
    if (!vendorId) return;
    const payload = buildPayload();
    if (!payload) return;

    try {
      setSaving(true);
      if (editing) {
        await updateAmbulanceServiceArea(vendorId, editing.serviceAreaId, payload);
        toast.success('Service area updated.');
      } else {
        await createAmbulanceServiceArea(vendorId, payload);
        toast.success('Service area added.');
      }
      setModalOpen(false);
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (row: AmbulanceVendorServiceArea) => {
    if (!vendorId) return;
    if (!window.confirm(`Remove this service area (${row.pinCode || row.areaName || row.serviceAreaId})?`)) return;
    try {
      await deleteAmbulanceServiceArea(vendorId, row.serviceAreaId);
      toast.success('Removed.');
      await load();
    } catch (e: unknown) {
      const err = e as { response?: { data?: { message?: string } } };
      toast.error(err?.response?.data?.message || 'Delete failed.');
    }
  };

  return (
    <div className="w-full min-w-0 max-w-5xl mx-auto p-3 sm:p-4 md:p-6">
      <Toaster position="top-right" />
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <button
          type="button"
          onClick={() => navigate(`/vendors/ambulance/${vendorId}`)}
          className="inline-flex items-center text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-white"
        >
          <ArrowLeftIcon className="h-5 w-5 mr-2" />
          Back to vendor dashboard
        </button>
        <button
          type="button"
          onClick={openAdd}
          className="inline-flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
        >
          <PlusIcon className="h-4 w-4" />
          Add pincode / area
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
        <div className="px-6 py-5 border-b dark:border-gray-700 flex items-start gap-3">
          <div className="p-2 rounded-lg bg-emerald-50 dark:bg-emerald-900/30">
            <MapPinIcon className="h-7 w-7 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <h1 className="text-xl font-semibold text-gray-900 dark:text-white">Service areas</h1>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">
              Pincodes and optional area names where this agency receives requests. You can also use a map radius
              (lat, lng, radius km) instead of or in addition to a pincode.
            </p>
          </div>
        </div>

        <div className="p-6">
          {loading ? (
            <p className="text-center text-gray-500 py-8">Loading…</p>
          ) : rows.length === 0 ? (
            <p className="text-center text-gray-500 py-8">No service areas yet. Add a pincode or geo coverage.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left border-b border-gray-200 dark:border-gray-700">
                    <th className="py-2 pr-4">Pincode</th>
                    <th className="py-2 pr-4">Area name</th>
                    <th className="py-2 pr-4">Lat</th>
                    <th className="py-2 pr-4">Lng</th>
                    <th className="py-2 pr-4">Radius km</th>
                    <th className="py-2 pr-4">Active</th>
                    <th className="py-2 pr-1 text-right">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((r) => (
                    <tr key={r.serviceAreaId} className="border-b border-gray-100 dark:border-gray-800">
                      <td className="py-2 pr-4 font-mono">{r.pinCode ?? '—'}</td>
                      <td className="py-2 pr-4">{r.areaName ?? '—'}</td>
                      <td className="py-2 pr-4">{r.latitude ?? '—'}</td>
                      <td className="py-2 pr-4">{r.longitude ?? '—'}</td>
                      <td className="py-2 pr-4">{r.radiusKm ?? '—'}</td>
                      <td className="py-2 pr-4">{r.isActive ? 'Yes' : 'No'}</td>
                      <td className="py-2 pr-1 text-right whitespace-nowrap">
                        <button
                          type="button"
                          onClick={() => openEdit(r)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs border border-indigo-200 text-indigo-700 mr-2"
                        >
                          <PencilSquareIcon className="h-3.5 w-3.5" />
                          Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(r)}
                          className="inline-flex items-center gap-1 rounded-md px-2 py-1 text-xs border border-red-200 text-red-700"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 z-[60] flex items-end justify-center overflow-y-auto bg-black/50 p-2 sm:items-center sm:p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl max-w-lg w-full">
            <div className="p-5 border-b dark:border-gray-700 flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                {editing ? 'Edit service area' : 'Add service area'}
              </h3>
              <button
                type="button"
                className="text-gray-400 hover:text-gray-600"
                onClick={() => !saving && setModalOpen(false)}
              >
                ×
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Pincode (digits)</label>
                <input
                  value={form.pinCode}
                  onChange={(e) => setForm((f) => ({ ...f, pinCode: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="411001"
                  maxLength={10}
                />
              </div>
              <div>
                <label className="block text-sm text-gray-600 dark:text-gray-300 mb-1">Area name</label>
                <input
                  value={form.areaName}
                  onChange={(e) => setForm((f) => ({ ...f, areaName: e.target.value }))}
                  className="w-full border rounded-md px-3 py-2 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  placeholder="Kothrud"
                />
              </div>
              <p className="text-xs text-gray-500">Optional geo coverage (all three required if used):</p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Latitude</label>
                  <input
                    value={form.latitude}
                    onChange={(e) => setForm((f) => ({ ...f, latitude: e.target.value }))}
                    className="w-full border rounded-md px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Longitude</label>
                  <input
                    value={form.longitude}
                    onChange={(e) => setForm((f) => ({ ...f, longitude: e.target.value }))}
                    className="w-full border rounded-md px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Radius (km)</label>
                  <input
                    value={form.radiusKm}
                    onChange={(e) => setForm((f) => ({ ...f, radiusKm: e.target.value }))}
                    className="w-full border rounded-md px-2 py-1.5 text-sm dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  />
                </div>
              </div>
              <label className="inline-flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm((f) => ({ ...f, isActive: e.target.checked }))}
                />
                Active
              </label>
            </div>
            <div className="px-5 py-4 border-t dark:border-gray-700 flex justify-end gap-2">
              <button
                type="button"
                disabled={saving}
                onClick={() => setModalOpen(false)}
                className="px-4 py-2 text-sm rounded-md bg-gray-100 dark:bg-gray-700"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={saving}
                onClick={handleSave}
                className="px-4 py-2 text-sm rounded-md bg-blue-600 text-white disabled:opacity-50"
              >
                {saving ? 'Saving…' : editing ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AmbulanceVendorServiceAreasPage;
